import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../lib/config';

const AuthContext = createContext(null);

// Send cookies with cross-origin requests (needed if preview URL differs from API origin)
axios.defaults.withCredentials = true;

/** Set or clear the Bearer token used for all subsequent API calls. */
function applyToken(token) {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // BACKEND_URL, axios, applyToken, setUser are all stable module/useState references —
  // they never change between renders, so the empty dep array is correct.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const logout = useCallback(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`);
    } catch (err) {
      // Logout API errors are non-fatal — the local session is cleared regardless
      console.error('Logout API error (session still cleared):', err?.response?.status || err?.message);
    }
    applyToken(null);
    setUser(null);
  }, []);

  // BACKEND_URL, axios, applyToken, setUser, setLoading are stable module/useState refs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchMe = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/me`);
      const { _session_token, ...userData } = res.data;
      // Re-apply Bearer token in memory so API calls don't rely solely on the cookie
      if (_session_token) applyToken(_session_token);
      setUser(userData);
    } catch {
      // No valid session cookie — not an error condition, just redirect to login
      applyToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // On mount, validate existing session (via httpOnly cookie)
  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const res = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password });
    const { access_token, user: userData } = res.data;
    // Set Bearer header in memory for this session (secure: not in localStorage)
    applyToken(access_token);
    setUser(userData);
    return userData;
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, isAdmin: user?.role === 'admin' }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
